import { useState, useEffect, useRef } from "react";
import { Footer, Composer, NewChatButton, svgPaths } from "./shared";
import { getTrendingQueries } from "./chatbotService";

// ─── Home: Suggestion Cards ───────────────────────────────────────────────────

function SuggestionCard({ icon, text, onClick }) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const bg = hovered || pressed ? "#d8e5fd" : "#f4f7fc";
  const pressedGradient = "linear-gradient(90deg, #188CE5 0%, #9D7CD4 33%, #FF32FF 66%, #27ACAA 100%)";
  const borderStyle = pressed
    ? { border: "1px solid transparent", background: `linear-gradient(#d8e5fd, #d8e5fd) padding-box, ${pressedGradient} border-box` }
    : { border: "1px solid #afcdfb", backgroundColor: bg };
  return (
    <div
      role="button" tabIndex={0}
      onClick={() => onClick(text)}
      onKeyDown={(e) => e.key === "Enter" && onClick(text)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{ flex: "1 0 0", height: 80, borderRadius: "var(--border-radius-lg, 8px)", ...borderStyle, minWidth: 0, cursor: "pointer", transition: "background-color 0.15s ease, border-color 0.15s ease", display: "flex", alignItems: "center", gap: "var(--spacing-12, 12px)", padding: "var(--spacing-8, 8px) var(--spacing-16, 16px)" }}
    >
      {icon}
      <p style={{ flex: "1 0 0", fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "1.5", color: "var(--text-colors-default-primary, #2e2e38)", margin: 0 }}>{text}</p>
    </div>
  );
}

function TrendIcon() {
  return <div style={{ position: "relative", width: 18, height: 18, flexShrink: 0 }}><svg style={{ position: "absolute", inset: "13%", width: "74%", height: "74%" }} fill="none" viewBox="0 0 20.4167 20.4167" preserveAspectRatio="none"><path clipRule="evenodd" d={svgPaths.p2eae9380} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /><path clipRule="evenodd" d={svgPaths.p21cb9e80} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /></svg></div>;
}
function LanguageIcon() {
  return <div style={{ position: "relative", width: 18, height: 18, flexShrink: 0 }}><svg style={{ position: "absolute", inset: "5%", width: "90%", height: "90%" }} fill="none" viewBox="0 0 17.9167 17.9167" preserveAspectRatio="none"><path clipRule="evenodd" d={svgPaths.p3a335780} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /><path clipRule="evenodd" d={svgPaths.p2ac0f6f0} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /><path clipRule="evenodd" d={svgPaths.p187f1a80} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /><path clipRule="evenodd" d={svgPaths.p2074ae00} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /><path clipRule="evenodd" d={svgPaths.p3423e700} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /></svg></div>;
}
function PageIcon() {
  return <div style={{ position: "relative", width: 18, height: 18, flexShrink: 0 }}><svg style={{ position: "absolute", top: "5%", bottom: "5%", left: "13%", right: "13%", width: "74%", height: "90%" }} fill="none" viewBox="0 0 14.5833 17.9167" preserveAspectRatio="none"><path clipRule="evenodd" d={svgPaths.pbd05180} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /><path clipRule="evenodd" d={svgPaths.p1ad8c800} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /><path clipRule="evenodd" d={svgPaths.p38674f0} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /><path clipRule="evenodd" d={svgPaths.p3244d00} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /><path clipRule="evenodd" d={svgPaths.p32021800} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" /></svg></div>;
}

// ─── Home View ─────────────────────────────────────────────────────────────────

const DEFAULT_PROMPTS = [
  
];

const PROMPT_ICONS = [<TrendIcon />, <LanguageIcon />, <PageIcon />];

export function HomeView({ inputText, onInputChange, onSubmit, onNewChat }) {
  const handleSuggestionClick = (text) => onInputChange(text);
  const [prompts, setPrompts] = useState(DEFAULT_PROMPTS);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    getTrendingQueries(4)
      .then((queries) => {
        if (mountedRef.current && queries.length) setPrompts(queries.slice(0, 4));
      })
      .catch(() => {});
    return () => { mountedRef.current = false; };
  }, []);

  return (
    <div style={{ flex: "1 0 0", position: "relative", width: "100%", display: "flex", flexDirection: "column", padding: "var(--spacing-20, 20px)", gap: "var(--spacing-16, 16px)", minHeight: 0 }}>
      {/* Main card */}
      <div style={{ flex: "1 0 0", borderRadius: "var(--border-radius-lg, 8px)", backgroundColor: "rgba(255,255,255,0.4)", border: "1.5px solid rgba(255,255,255,1)", display: "flex", flexDirection: "column", gap: "var(--spacing-12, 12px)", padding: "var(--spacing-20, 20px)", position: "relative", minHeight: 0 }}>
        {/* Title row */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", width: "100%", gap: "var(--spacing-16, 16px)", flexShrink: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-4, 4px)" }}>
            <h1 style={{ fontWeight: 400, fontSize: "var(--font-size-28, 1.75rem)", lineHeight: "1.3", letterSpacing: "-0.4px", color: "var(--eyp-color-blue-900-dark-blue, #072e65)", margin: 0, textAlign:"left" }}>DBS AI Chatbot</h1>
            <p style={{ fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "1.5", color: "var(--text-colors-default-primary, #2e2e38)", margin: 0 }}>Ask questions on DBS datasets and get AI-generated insights from approved document extracts.</p>
          </div>
          <NewChatButton onClick={onNewChat} />
        </div>

        {/* Welcome + suggestion cards */}
        <div style={{ flex: "1 0 0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "var(--spacing-12, 12px)", width: "100%", minHeight: 0 }}>
          <p style={{ fontWeight: 400, fontSize: "var(--font-size-16, 1rem)", color: "var(--eyp-color-blue-900-dark-blue, #072e65)", margin: 0, textAlign: "center" }}>How can I help you?</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-8, 8px)", width: "min(700px, 100%)" }}>
            {Array.from({ length: Math.ceil(prompts.length / 2) }).map((_, row) => (
              <div key={row} style={{ display: "flex", gap: "var(--spacing-8, 8px)" }}>
                {prompts.slice(row * 2, row * 2 + 2).map((prompt, col) => {
                  const index = row * 2 + col;
                  return (
                    <SuggestionCard
                      key={`${index}-${prompt}`}
                      icon={PROMPT_ICONS[index % PROMPT_ICONS.length]}
                      text={prompt}
                      onClick={handleSuggestionClick}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Composer */}
        <Composer value={inputText} onChange={onInputChange} onSubmit={onSubmit} appState="idle" />
      </div>

      <Footer />
    </div>
  );
}
